/**
 * Utility functions for determining survey option types
 * These functions replace the redundant is_text property with precise type checking
 */

/**
 * Local copy to avoid dependencies with legacy code
 */
export enum ESurveysOptionType {
  RADIO = "Survey::Option::Radio",
  TEXTAREA = "Survey::Option::Textarea",
  TEXTFIELD = "Survey::Option::Textfield",
}

// Survey option type constants matching the enum values
export const SURVEY_OPTION_TYPES = {
  TEXTFIELD: "Survey::Option::Textfield" as ESurveysOptionType.TEXTFIELD,
  TEXTAREA: "Survey::Option::Textarea" as ESurveysOptionType.TEXTAREA,
  RADIO: "Survey::Option::Radio" as ESurveysOptionType.RADIO,
} as const;

// Type definition for survey options used in these utilities
// This is more flexible than MSurveysQuestionOption to handle partial data
export type SurveyOption = {
  id?: number;
  text?: string;
  type?: ESurveysOptionType | string;
  isText?: boolean; // Newer camelCase version
};

/**
 * Checks if an option is any type of text input (textfield or textarea)
 * @param option - Survey option object
 * @returns True if option is text-based
 */
export const isTextOption = (
  option: SurveyOption | null | undefined,
): boolean => {
  if (!option) return false;
  return (
    option.type === SURVEY_OPTION_TYPES.TEXTFIELD ||
    option.type === SURVEY_OPTION_TYPES.TEXTAREA
  );
};

/**
 * Checks if an option is specifically a textfield (single-line text input)
 * @param option - Survey option object
 * @returns True if option is a textfield
 */
export const isTextFieldOption = (
  option: SurveyOption | null | undefined,
): boolean => {
  return Boolean(option && option.type === SURVEY_OPTION_TYPES.TEXTFIELD);
};

/**
 * Checks if an option is specifically a textarea (multi-line text input)
 * @param option - Survey option object
 * @returns True if option is a textarea
 */
export const isTextareaOption = (
  option: SurveyOption | null | undefined,
): boolean => {
  return Boolean(option && option.type === SURVEY_OPTION_TYPES.TEXTAREA);
};

/**
 * Checks if an option is a radio/choice option
 * @param option - Survey option object
 * @returns True if option is a choice option
 */
export const isChoiceOption = (
  option: SurveyOption | null | undefined,
): boolean => {
  return Boolean(option && option.type === SURVEY_OPTION_TYPES.RADIO);
};

/**
 * Checks if an option should be represented with a word cloud
 * (only textfield options get word cloud representation)
 * @param option - Survey option object
 * @returns True if option should show word cloud
 */
export const shouldShowWordCloud = (
  option: SurveyOption | null | undefined,
): boolean => {
  return isTextFieldOption(option);
};
